import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONTS } from '../constants/theme';

export interface HealthBarOptions {
  width: number;
  height: number;
  currentHealth: number;
  maxHealth: number;
  showText?: boolean;
  showMaxHealth?: boolean; // If false, only show current health (not "current / max")
}

/**
 * Health bar component with color-coded fill based on health percentage
 * Uses Graphics directly for reliable color transitions
 */
export class HealthBar extends Container {
  private background: Graphics;
  private fill: Graphics;
  private healthText: Text | null = null;
  private barWidth: number;
  private barHeight: number;
  private currentHealth: number;
  private maxHealth: number;
  private showMaxHealth: boolean;

  constructor(options: HealthBarOptions) {
    super();

    const { width, height, currentHealth, maxHealth, showText = true, showMaxHealth = true } = options;

    this.barWidth = width;
    this.barHeight = height;
    this.currentHealth = currentHealth;
    this.maxHealth = maxHealth;
    this.showMaxHealth = showMaxHealth;

    // Background
    this.background = new Graphics();
    this.addChild(this.background);

    // Fill
    this.fill = new Graphics();
    this.addChild(this.fill);

    // Draw initial state
    this.draw();

    // Health text (optional)
    if (showText) {
      const textStyle = new TextStyle({
        fontFamily: FONTS.PRIMARY,
        fontSize: FONTS.SIZES.XS,
        fontWeight: 'bold',
        fill: COLORS.TEXT_PRIMARY,
      });
      this.healthText = new Text({ text: this.getHealthText(), style: textStyle });
      this.healthText.anchor.set(0.5);
      this.healthText.x = width / 2;
      this.healthText.y = height / 2;
      this.addChild(this.healthText);
    }
  }

  private getPercent(): number {
    return Math.max(0, Math.min(1, this.currentHealth / this.maxHealth));
  }

  private getHealthText(): string {
    if (this.showMaxHealth) {
      return `${this.currentHealth} / ${this.maxHealth}`;
    }
    return `${this.currentHealth}`;
  }

  private getHealthColor(percent: number): number {
    if (percent > 0.6) return COLORS.HEALTH_HIGH;
    if (percent > 0.3) return COLORS.HEALTH_MEDIUM;
    return COLORS.HEALTH_LOW;
  }

  private draw(): void {
    const percent = this.getPercent();
    const fillWidth = this.barWidth * percent;

    // Background
    this.background.clear();
    this.background.roundRect(0, 0, this.barWidth, this.barHeight, this.barHeight / 2);
    this.background.fill({ color: COLORS.BG_TERTIARY });

    // Fill
    this.fill.clear();
    if (fillWidth > 0) {
      this.fill.roundRect(0, 0, fillWidth, this.barHeight, this.barHeight / 2);
      this.fill.fill({ color: this.getHealthColor(percent) });
    }
  }

  /**
   * Update health values with animation
   */
  setHealth(current: number, max: number): void {
    const oldPercent = this.getPercent();
    this.currentHealth = current;
    this.maxHealth = max;
    const newPercent = this.getPercent();

    // Update text immediately
    if (this.healthText) {
      this.healthText.text = this.getHealthText();
    }

    // Animate the change
    const obj = { percent: oldPercent };
    gsap.to(obj, {
      percent: newPercent,
      duration: 0.3,
      onUpdate: () => {
        const fillWidth = this.barWidth * obj.percent;
        this.fill.clear();
        if (fillWidth > 0) {
          this.fill.roundRect(0, 0, fillWidth, this.barHeight, this.barHeight / 2);
          this.fill.fill({ color: this.getHealthColor(obj.percent) });
        }
      },
    });
  }
}
